// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SettingsByRange.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * SettingsByRangeItem component
 */
import { observer } from 'mobx-react';
import React, { Component } from 'react';
import { isFunction, assign, get, isEqual, isNil } from 'lodash/fp';

import Constants from 'logic/data/Izenda.SettingsByRangeConstants';
import InputByDataType from 'share/components/controls/Izenda.InputByDataType';
import GridLayout from 'share/components/controls/Izenda.GridLayout';

const DATA_TYPE = Constants.DATA_TYPE;

@observer
export default class SettingsByRangeItem extends Component {
  state = {
    overHandle: false
  };

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    this.setState({
      overHandle: false
    });
  }

  render() {
    const { connectDragSource } = this.props;
    const isDraggableEnabled = !isNil(connectDragSource);
    const { overHandle } = this.state;

    return (isDraggableEnabled && overHandle)
      ? connectDragSource(this.renderComponent())
      : this.renderComponent();
  }

  renderComponent() {
    const { itemData, onDelete, canMove } = this.props;
    const { isOver } = this.props; // is dragging over itself

    // For validation
    this.validateFromCtrlId = `${itemData.id}_from`;
    this.validateToCtrlId = `${itemData.id}_to`;
    this.validateValueCtrlId = `${itemData.id}_value`;
    this.validateNameCtrlId = `${itemData.id}_name`;

    const hoverStyle = {
      opacity: isOver ? 0 : 1,
    };
    return (
      <tr style={hoverStyle}>
        {canMove && (
          <td className="u_alignMiddle"
            onMouseEnter={() => this.setState({ overHandle: true }) }
            onMouseLeave={() => this.setState({ overHandle: false }) }>
            <span className="icon-drag-handle"/>
          </td>
        )}
        {this.renderName()}
        <td className="u_alignMiddle">{this.renderRangeCtrl()}</td>
        <td className="u_alignMiddle">{this.renderValueCtrl(this.validateValueCtrlId)}</td>
        {itemData.canDelete && (
          <td className="u_alignMiddle">
            <span onClick={onDelete} className="icon-cancel" />
          </td>
        )}
      </tr>);
  }

  renderName() {
    const { itemData, valueType, haveName } = this.props;

    const isEditName = haveName && (valueType === 'rangeValue' || valueType === 'rangePercent');

    const nameProps = {
      type: 'text',
      value: itemData.name,
      validations: Constants.CONSTRAINTS,
      field: this.validateNameCtrlId,
      onChange: handleChange
    };

    const name = isEditName ? <InputByDataType {...nameProps} /> : <span>{itemData.name}</span>;

    return haveName ? <td>{name}</td> : null;

    function handleChange(newVal) {
      itemData.name = newVal.value;
    }
  }

  renderRangeCtrl() {
    const { itemData, valueType, dataType, dateTimeFormatData } = this.props;

    const inputType = this.getInputTypeByDataType(dataType);
    const dateTimeFormatDataResult = this.getDateTimeFormatData(dateTimeFormatData);
    const inputControlProps = {
      groupByDateTime: get('groupBy', dateTimeFormatDataResult)
    };

    switch (valueType) {
      case 'value':
        return this.renderInputCtrl((isEqual(itemData.name, 'High') || isEqual(itemData.name, 'Low'))
          ? 'number' : inputType, itemData, 'key', itemData.id, inputControlProps);
      case 'rangeValue':
        return (
          <GridLayout>
            <GridLayout.Row>
              <GridLayout.Col cols={5}>
                {this.renderInputCtrl(inputType, itemData, 'from', this.validateFromCtrlId, inputControlProps)}
              </GridLayout.Col>
              <GridLayout.Col cols={2}>
                <span>-</span>
              </GridLayout.Col>
              <GridLayout.Col cols={5}>
                {this.renderInputCtrl(inputType, itemData, 'to', this.validateToCtrlId, inputControlProps)}
              </GridLayout.Col>
            </GridLayout.Row>
          </GridLayout>
        );
      case 'rangePercent':
        return (
          <GridLayout>
            <GridLayout.Row>
              <GridLayout.Col cols={5}>
                <div className="input-group">
                  {this.renderInputCtrl(inputType, itemData, 'from', this.validateFromCtrlId, inputControlProps)}
                  <span className="input-group-addon">%</span>
                </div>
              </GridLayout.Col>
              <GridLayout.Col cols={2} className="u_controlHeight">
                <span>-</span>
              </GridLayout.Col>
              <GridLayout.Col cols={5}>
                <div className="input-group">
                  {this.renderInputCtrl(inputType, itemData, 'to', this.validateToCtrlId, inputControlProps)}
                  <span className="input-group-addon">%</span>
                </div>
              </GridLayout.Col>
            </GridLayout.Row>
          </GridLayout>
        );
      default:
      // do nothing
    }
  }

  renderValueCtrl(validateId) {
    const { itemData } = this.props;
    
    const valueProps = this.props.valueProps || {};

    return this.renderInputCtrl(
      valueProps.type,
      itemData,
      'text',
      validateId,
      assign({ className: 'value-container' }, valueProps)
    );
  }

  renderInputCtrl(dataType, source, valueKey, validateId, inputCtrlProps) {
    const { valueType } = this.props;

    prepareProps();
    return <InputByDataType {...inputCtrlProps} type={dataType || 'text'} onChange={changeValue} />;

    function changeValue(newVal) {
      source[valueKey] = newVal.value;
    }

    function prepareProps() {
      inputCtrlProps = assign(inputCtrlProps, { value: source[valueKey] });
      dataType === 'number' && source[valueKey] === '' && (inputCtrlProps = assign(inputCtrlProps, { value: null }));
      if (valueType === 'rangePercent' && dataType === 'number') {
        inputCtrlProps = assign(inputCtrlProps, { min: 0 });
      } else if (dataType === 'select') {
        inputCtrlProps = assign(inputCtrlProps, Constants.BOOLEAN_OPTION);
      }

      if (dataType !== 'color') {
        inputCtrlProps = assign(inputCtrlProps, { validations: Constants.CONSTRAINTS, field: validateId });
      }

      // for Value with High/Low name >> do not require value (map > shading config, should be use through compoent-props)
      if (valueType === 'value' && ['High', 'Low'].indexOf(source.name) > -1 && source.text) {
        delete inputCtrlProps.validations;
      }
    }
  }

  getInputTypeByDataType(dataType) {
    switch (dataType) {
      case DATA_TYPE.TEXT:
        return 'text';
      case DATA_TYPE.MONEY:
      case DATA_TYPE.NUMERIC:
        return 'number';
      case DATA_TYPE.BOOLEAN:
        return 'select';
      case DATA_TYPE.DATETIME:
        return 'datetime';
      case DATA_TYPE.TIME:
        return 'time';
      default:
        return 'text';
    }
  }

  getDateTimeFormatData(dateTimeFormatDataFn) {
    return isFunction(dateTimeFormatDataFn) ? dateTimeFormatDataFn() : dateTimeFormatDataFn;
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.SettingsByRangeItem.js